import { DocumentRecycleBin } from "./DocumentRecycleBin";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronDown, Plus, Search } from "lucide-react";
import { useState, type ReactNode } from "react";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import type { OpsCustomerSummary, OpsPermission } from "../../shared/operations-schemas";
import type { ToolHub } from "../../shared/tool-hubs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { CellTitle, RowTable, type RowColumn } from "../components/ui/row-table";
import { listCommercialDocuments } from "./commercial-documents-api";
import { CustomerContext } from "./CustomerContext";
import {
  abilities,
  documentRows,
  hubName,
  type DocumentAbilities as Can,
  type DocumentDialog as Dialog,
  type DocumentRow,
} from "./customer-document-rows";
import { DocumentsPanel } from "./CustomerDocumentSheet";
import { listCustomerLibrary } from "./customer-documents-api";
import { dayOf } from "./customer-page-format";
import type { DocumentFilter } from "./customer-route";
import type { OperationsNavigate } from "./navigation";
import { FilterChips } from "./ToolHubPage";
import { customerPageKeys, getHubAssignments, listToolHubs } from "./tool-hubs-api";

type Props = {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  navigate: OperationsNavigate;
  view: "documents" | "notes";
  initialFilter: DocumentFilter;
};
type Chip = DocumentFilter | "notes";
const CHIPS: { key: Chip; label: string }[] = [
  { key: "all", label: "All" },
  { key: "contracts", label: "Contracts" },
  { key: "knowledge", label: "Knowledge" },
  { key: "notes", label: "Team notes & files" },
];

function useDocumentData(
  customer: OpsCustomerSummary,
  search: string,
  archived: boolean,
  can: Can,
) {
  const library = useInfiniteQuery({
    queryKey: ["ops", "customer-library", customer.slug, "all", search, archived],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listCustomerLibrary(customer.slug, {
        search,
        archived: String(archived),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: can.readKnowledge || can.readCommercial,
  });
  const generated = useInfiniteQuery({
    queryKey: ["ops", "commercial-documents", customer.slug, search],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listCommercialDocuments(customer.slug, search, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: can.readCommercial && !archived,
  });
  const hubs = useQuery({
    queryKey: ["ops", "tool-hubs", customer.id],
    queryFn: () => listToolHubs(customer.id),
    enabled: can.readKnowledge,
  });
  const assignments = useQuery({
    queryKey: customerPageKeys.assignments(customer.id),
    queryFn: () => getHubAssignments(customer.id),
    enabled: can.readKnowledge,
  });
  return { library, generated, hubs, assignments };
}

export function CustomerDocumentsTab({
  customer,
  permissions,
  navigate,
  view,
  initialFilter,
}: Props) {
  const can = abilities(permissions);
  const [filter, setFilter] = useState<DocumentFilter>(initialFilter);
  const pick = (chip: Chip) => {
    if (chip === "notes") navigate(`/customers/${customer.slug}/notes`);
    else {
      setFilter(chip);
      if (view === "notes") navigate(`/customers/${customer.slug}/documents`);
    }
  };
  return (
    <section aria-labelledby="customer-documents-title" className="space-y-5">
      <h2 id="customer-documents-title" className="sr-only">
        Documents
      </h2>
      {view === "notes" ? (
        <>
          <FilterChips options={CHIPS} value="notes" onChange={pick} label="Show" />
          <CustomerContext
            customerSlug={customer.slug}
            navigate={navigate}
            organizationId={customer.id}
            ready={customer.provisioningStatus === "ready"}
            canRead={can.readKnowledge}
            canReadArtifacts={permissions.has("artifact.read")}
            canWrite={can.writeKnowledge}
            canSearch={permissions.has("context.search")}
            canUploadArtifacts={permissions.has("artifact.upload")}
          />
        </>
      ) : (
        <DocumentList
          customer={customer}
          can={can}
          filter={filter}
          onFilter={pick}
          navigate={navigate}
        />
      )}
    </section>
  );
}

type ListState = ReturnType<typeof useListState>;
function useListState(customer: OpsCustomerSummary, can: Can, filter: DocumentFilter) {
  const [search, setSearch] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [archived, setArchived] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const data = useDocumentData(customer, submitted, archived, can);
  const generated = archived ? [] : (data.generated.data?.pages ?? []);
  const rows = documentRows({
    library: data.library.data?.pages.flatMap((page) => page.documents) ?? [],
    generated: generated.flatMap((page) => page.documents),
    assignments: data.assignments.data,
  }).filter((row) => filter === "all" || row.group === filter);
  const waitingForGenerated = can.readCommercial && !archived && data.generated.isPending;
  /** A library row opens the document itself; a contract opens its facts first. */
  const openRow = (row: DocumentRow) => {
    const library = row.source.kind === "library" ? row.source.document : null;
    setSelected(library ? null : row.key);
    setDialog(library ? { kind: library.draft.fields.kind, document: library } : null);
  };
  return {
    openRow,
    hubIdOf: (documentId: string) =>
      data.assignments.data?.library.find((row) => row.documentId === documentId)?.hubId ?? null,
    search,
    setSearch,
    submitted,
    setSubmitted,
    archived,
    setArchived,
    selected,
    setSelected,
    dialog,
    setDialog,
    data,
    rows,
    hubs: data.hubs.data?.hubs ?? [],
    loading: data.library.isPending || waitingForGenerated,
    error: data.library.error ?? data.generated.error,
  };
}

function DocumentList({
  customer,
  can,
  filter,
  onFilter,
  navigate,
}: {
  customer: OpsCustomerSummary;
  can: Can;
  filter: DocumentFilter;
  onFilter: (chip: Chip) => void;
  navigate: OperationsNavigate;
}) {
  const state = useListState(customer, can, filter);
  if (!can.readKnowledge && !can.readCommercial)
    return (
      <p className="text-muted-foreground">You don’t have access to this customer’s documents.</p>
    );
  return (
    <>
      <DocumentsToolbar
        state={state}
        filter={filter}
        onFilter={onFilter}
        newMenu={
          <>
            <DocumentRecycleBin slug={customer.slug} />
            <NewDocumentMenu
              can={can}
              slug={customer.slug}
              navigate={navigate}
              onLibrary={state.setDialog}
            />
          </>
        }
      />
      {state.loading ? <ContentLoading label="Loading documents" /> : null}
      {state.error ? (
        <div className="space-y-3">
          <FieldError>{state.error.message}</FieldError>
          <Button
            variant="secondary"
            onClick={() => {
              void state.data.library.refetch();
              void state.data.generated.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      ) : null}
      {!state.loading ? (
        <RowTable
          label="Documents"
          columns={documentColumns(state.hubs)}
          rows={state.rows}
          rowKey={(row) => row.key}
          rowLabel={(row) => `${row.title}, ${row.detail}`}
          selectedKey={state.selected}
          onOpen={state.openRow}
          empty={state.submitted ? "No documents match that search." : "No documents here yet."}
        />
      ) : null}
      <MoreDocuments data={state.data} archived={state.archived} />
      <DocumentsPanel
        context={{ customer, can, hubs: state.hubs, navigate }}
        row={state.rows.find((row) => row.key === state.selected) ?? null}
        dialog={state.dialog}
        hubIdOf={state.hubIdOf}
        onDialog={state.setDialog}
        onClose={() => {
          state.setSelected(null);
          state.setDialog(null);
        }}
      />
    </>
  );
}

function DocumentsToolbar({
  state,
  filter,
  onFilter,
  newMenu,
}: {
  state: ListState;
  filter: DocumentFilter;
  onFilter: (chip: Chip) => void;
  newMenu: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <form
        role="search"
        className="relative w-full sm:w-80"
        onSubmit={(event) => {
          event.preventDefault();
          state.setSubmitted(state.search.trim());
        }}
      >
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label="Search documents by title or reference"
          className="pl-9"
          placeholder="Search every document"
          type="search"
          value={state.search}
          onChange={(event) => {
            state.setSearch(event.target.value);
            if (!event.target.value) state.setSubmitted("");
          }}
        />
      </form>
      <FilterChips options={CHIPS} value={filter} onChange={onFilter} label="Show" />
      <Button
        variant={state.archived ? "secondary" : "ghost"}
        size="sm"
        aria-pressed={state.archived}
        onClick={() => {
          state.setArchived(!state.archived);
        }}
      >
        Archived
      </Button>
      <span className="flex-1" />
      {newMenu}
    </div>
  );
}

function documentColumns(hubs: readonly ToolHub[]): RowColumn<DocumentRow>[] {
  return [
    {
      key: "document",
      header: "Document",
      cell: (row) => <CellTitle title={row.title} detail={row.detail} />,
    },
    {
      key: "tool",
      header: "Tool",
      className: "hidden md:table-cell text-muted-foreground",
      cell: (row) => hubName(hubs, row.hubId),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <Badge variant={row.status.variant}>{row.status.label}</Badge>,
    },
    {
      key: "visible",
      header: "Customer can see",
      className: "hidden lg:table-cell text-muted-foreground",
      cell: (row) => row.visible,
    },
    {
      key: "updated",
      header: "Updated",
      className: "hidden sm:table-cell text-muted-foreground whitespace-nowrap",
      cell: (row) => dayOf(row.updatedAt),
    },
  ];
}

function MoreDocuments({
  data,
  archived,
}: {
  data: ReturnType<typeof useDocumentData>;
  archived: boolean;
}) {
  const more = data.library.hasNextPage || (!archived && data.generated.hasNextPage);
  if (!more) return null;
  return (
    <Button
      variant="secondary"
      disabled={data.library.isFetchingNextPage || data.generated.isFetchingNextPage}
      onClick={() => {
        if (data.library.hasNextPage) void data.library.fetchNextPage();
        if (!archived && data.generated.hasNextPage) void data.generated.fetchNextPage();
      }}
    >
      Load more documents
    </Button>
  );
}

function NewDocumentMenu({
  can,
  slug,
  navigate,
  onLibrary,
}: {
  can: Can;
  slug: string;
  navigate: OperationsNavigate;
  onLibrary: (dialog: Dialog) => void;
}) {
  if (!can.writeKnowledge && !can.manageCommercial) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button />}>
        <Plus /> New document <ChevronDown />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {can.manageCommercial
          ? (["order_form", "change_order", "quote"] as const).map((kind) => (
              <DropdownMenuItem
                key={kind}
                onClick={() => {
                  navigate(`/customers/${slug}/documents/new/${kind}`);
                }}
              >
                {COMMERCIAL_DOCUMENT_LABELS[kind]}
              </DropdownMenuItem>
            ))
          : null}
        {can.manageCommercial ? (
          <>
            <DropdownMenuItem
              onClick={() => {
                onLibrary({ kind: "agreement" });
              }}
            >
              Upload a signed agreement
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                onLibrary({ kind: "quote" });
              }}
            >
              Upload a quote
            </DropdownMenuItem>
          </>
        ) : null}
        {can.manageCommercial && can.writeKnowledge ? <DropdownMenuSeparator /> : null}
        {can.writeKnowledge ? (
          <DropdownMenuItem
            onClick={() => {
              onLibrary({ kind: "knowledge" });
            }}
          >
            Knowledge article
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
